/**
 * One site's security card data, and a scan on request.
 *
 * Read once when the page opens, and again when the site's package changes:
 * the scan runs weekly, so a minute's poll would show nothing new. While a
 * scan is going on (`running`, the server's judgement) the card asks every
 * `SECURITY_POLL_MS` for at most `SECURITY_POLL_LIMIT_MS` from when it first
 * saw the scan; after that it stops and says the scan is still going
 * (`stillRunning`), because a scan that died without storing anything must not
 * keep a tab polling for ever. That limit is judged on a fresh answer only
 * (`securityPollAfterAnswer`): a tab that was hidden past it reads once more
 * when it comes back, and shows a scan that finished meanwhile.
 *
 * The same race shape as `useReporter`: a late answer for an earlier read
 * never overwrites a newer one.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  SECURITY_POLL_MS,
  getSecurity,
  runSecurityNow,
  securityPollAfterAnswer,
  securityPollStep,
  type SecurityCardResponse,
} from "../../../../lib/monitoringSecurity";
import { useVisiblePoll } from "../useMonitors";

/** `allowed` is the page's reading of the package: when it changes (the package was edited), the card reads again. */
export function useSecurity(monitorId: string, allowed: boolean) {
  const [data, setData] = useState<SecurityCardResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [stillRunning, setStillRunning] = useState(false);
  const requestId = useRef(0);
  const alive = useRef(true);
  // When this card first saw the current scan; null while none is going on.
  const since = useRef<number | null>(null);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const rid = ++requestId.current;
    try {
      const res = await getSecurity(monitorId);
      if (!alive.current || rid !== requestId.current) return;
      // The window is judged on this fresh answer, never on the one before a hidden tab.
      const next = securityPollAfterAnswer(res.running, since.current, Date.now());
      since.current = next.since;
      setStillRunning(next.gaveUp);
      setData(res);
      setFailed(false);
    } catch {
      if (!alive.current || rid !== requestId.current) return;
      setFailed(true);
      // A read that keeps failing must not keep a tab asking for ever either.
      if (securityPollStep(true, since.current, Date.now()) === "gaveUp") setStillRunning(true);
    }
  }, [monitorId]);

  useEffect(() => {
    setData(null);
    setFailed(false);
    setStillRunning(false);
    since.current = null;
    void load();
  }, [load]);

  // Not on mount (the effect above reads already): only when the package changed since.
  const seenAllowed = useRef(allowed);
  useEffect(() => {
    if (seenAllowed.current === allowed) return;
    seenAllowed.current = allowed;
    void load();
  }, [allowed, load]);

  useVisiblePoll(Boolean(data?.running) && !stillRunning, SECURITY_POLL_MS, () => void load());

  /** Ask for a scan now. Throws the refusal (an `ApiError`) for the card to word. */
  const runNow = useCallback(async () => {
    await runSecurityNow(monitorId);
    // A new scan: a fresh polling window, whatever the last one ended as.
    since.current = Date.now();
    setStillRunning(false);
    await load();
  }, [monitorId, load]);

  /** Read again, and poll again if the scan is still going. */
  const reload = useCallback(async () => {
    since.current = null;
    setStillRunning(false);
    await load();
  }, [load]);

  return { data, failed, stillRunning, reload, runNow };
}
